import { DatabricksConfigType, OAUTH_TOKEN_CONFIG_TYPE, SnowflakeConfigType } from 'features/connections/types'

import { BigQueryConfigType } from 'features/connections/types'

import { ProviderType } from 'features/connections/types'

import { Connection } from 'features/connections/types'
import { ConnectionSharingOptions } from './ConnectionShareDialogUi'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { useMemo } from 'react'

export const isNewAccount = (accountCreated: string, deprecatedDate: string): boolean => {
  return new Date(accountCreated) >= new Date(deprecatedDate)
}

export function useConnectionTypes(
  enableSnowflakeRowLevelSecurity: boolean,
  databricksRestConnectionEnabled: string,
  connection?: Pick<Connection, 'id' | 'provider_id'> & ConnectionSharingOptions
) {
  const account = useGetAccountData()
  const accountCreated = account?.createdWhen

  const disableVCPAT = isNewAccount(accountCreated, databricksRestConnectionEnabled)

  const VIEWER_CREDENTIALS_SUPPORTED_PROVIDERS: ProviderType[] = [
    ProviderType.DATABRICKS,
    ProviderType.DATABRICKS_REST,
    ProviderType.DATABRICKS_WAREHOUSE,
    ProviderType.BIGQUERY
  ]

  // Added to both Service Accounts (will be disabled) and OAuth
  if (enableSnowflakeRowLevelSecurity) {
    VIEWER_CREDENTIALS_SUPPORTED_PROVIDERS.push(ProviderType.SNOWFLAKE)
  }

  const isBQWithSSO =
    connection?.provider_id === ProviderType.BIGQUERY && connection.config?.type === BigQueryConfigType.OAuthToken
  const isBQWithWIF =
    connection?.provider_id === ProviderType.BIGQUERY && connection.config?.type === BigQueryConfigType.WorkloadIdentity
  const isDatabricksWithU2m =
    connection?.provider_id === ProviderType.DATABRICKS_REST &&
    connection.config?.type === DatabricksConfigType.OAuthU2m
  const isDatabricksWithM2m =
    connection?.provider_id === ProviderType.DATABRICKS_REST &&
    connection.config?.type === DatabricksConfigType.OAuthM2m
  const isDatabricksRest =
    connection?.provider_id === ProviderType.DATABRICKS_REST && connection.config?.type === DatabricksConfigType.Rest
  const isSnowflakeWithSSO =
    enableSnowflakeRowLevelSecurity &&
    connection?.provider_id === ProviderType.SNOWFLAKE &&
    connection.config?.type === SnowflakeConfigType.OAuthToken
  const isSnowflakeWithKeyPair =
    connection?.provider_id === ProviderType.SNOWFLAKE && connection.config?.type === SnowflakeConfigType.KeyPair

  const isViewerCredentialsDisabledBigquery =
    connection?.provider_id === ProviderType.BIGQUERY && connection.config?.type === BigQueryConfigType.ServiceAccount
  const isViewerCredentialsDisabledSnowflake =
    connection?.provider_id === ProviderType.SNOWFLAKE && connection.config?.type !== OAUTH_TOKEN_CONFIG_TYPE
  const isViewerCredentialsDisabledDatabricks =
    connection?.provider_id === ProviderType.DATABRICKS_REST &&
    (connection.config?.type === DatabricksConfigType.OAuthM2m ||
      (connection.config?.type === DatabricksConfigType.Rest && disableVCPAT))

  return {
    isBQWithSSO,
    isBQWithWIF,
    isDatabricksWithU2m,
    isDatabricksWithM2m,
    isDatabricksRest,
    isSnowflakeWithSSO,
    isSnowflakeWithKeyPair,
    isViewerCredentialsDisabledBigquery,
    isViewerCredentialsDisabledSnowflake,
    isViewerCredentialsDisabledDatabricks,
    isViewerCredentialsDisabled:
      isViewerCredentialsDisabledBigquery ||
      isViewerCredentialsDisabledSnowflake ||
      isViewerCredentialsDisabledDatabricks,
    VIEWER_CREDENTIALS_SUPPORTED_PROVIDERS,
    providerSupportViewerCredentials:
      connection?.provider_id && VIEWER_CREDENTIALS_SUPPORTED_PROVIDERS.includes(connection?.provider_id),
    disabledEntireOrganization: isBQWithSSO || isSnowflakeWithSSO || isBQWithWIF || isDatabricksWithU2m,
    disabledGroups: isBQWithSSO || isSnowflakeWithSSO || isBQWithWIF || isDatabricksWithU2m,
    disableVCPAT
  }
}

export function useRequiresUserCredentials(
  allowSharing: boolean,
  connection: Pick<Connection, 'id' | 'provider_id'> & ConnectionSharingOptions
) {
  const shouldAllowSharing = useMemo(() => {
    // Databricks Spark ODBC connections are allowed to be shared, The option to share within the Organization,
    // in some cases has an additional radio button to share within the Organization asking for user credentials,
    // so the option to share within the Organizationmust be available:
    // - On Databricks connections, sharing a connection asking for the user's credentials is available.
    // - On Big Query connections using OAuth connection, sharing a connection requiring the user's credentials is the only option available.
    if (
      (connection?.provider_id === ProviderType.DATABRICKS &&
        connection.config?.type === DatabricksConfigType.SparkOdbc) ||
      (connection?.provider_id === ProviderType.BIGQUERY && connection.config?.type === BigQueryConfigType.OAuthToken)
    ) {
      return true
    }

    return allowSharing
  }, [connection?.provider_id, connection?.config?.type, allowSharing])

  return shouldAllowSharing
}
